import React from 'react';
import { routerMiddlewareHistory } from 'store';
import { getElementSizing } from 'utils/element';
import { addEventListener, removeEventListener } from 'utils/eventListener';
import { checkIfCanScroll } from 'utils/scroll';
import { noop } from 'utils/noop';
import Cell from './Cell';
import HeaderCell from './HeaderCell';
import Row from './Row';
import {
  EmptyContainer,
  EmptyRowNeededForFirefox,
  BodyContainer,
  MainCSSContainer,
} from './Body.styled';
import { didParamsChange, getTableHeight } from './Body.utils';

// type Props = {
//   /** Content to show in bulk actions. */
//   bulkActionsContent: node,
//   /** An additional table row class. */
//   className: string,
//   /** The component to use for the table cell in a row. */
//   CellComponent: React.Component,
//   /** An additional table cell class. */
//   cellClassName: string,
//   /**
//    * The columns of the table to generate the table headers and rows.
//    * See Table.js for additional details.
//    */
//   columns: [],
//   /** Docks table on bottom when table reaches bottom of screen. */
//   dockOnBottom: boolean,
//   /** Docks the left-most column. */
//   dockLeftColumn: boolean,
//   /** Props when displaying EmptyState. See: `EmptyState` component. */
//   emptyStateProps: {},
//   /** If previous column is expanded and should take over the space of entire row. */
//   expandColumn: ?boolean,
//   /** The component to use for the table header cells. */
//   HeaderCellComponent: React.Component,
//   /** An additional header table cell class. */
//   headerCellClassName: string,
//   /** An additional header table row class. */
//   headerRowClassName: string,
//   /** The component to use for the table header row. */
//   HeaderRowComponent: React.Component,
//   /** The history object. */
//   history: {},
//   /** Highlights rows on hover. */
//   hoverableRows: boolean,
//   /** Function to run when a sorting header is clicked. */
//   onChangeSort: (e: {}) => void,
//   /**
//    * Callback function when row is double clicked.
//    *
//    * ```
//    * onDoubleClickRow(e, rowProps) => {}
//    * ```
//    * @param { Object } e: The double click event.
//    * @param { Object } rowData: The data object of the row.
//    * @returns { void }
//    */
//   onDoubleClickRow: (e: {}, rowData: {}) => void,
//   /**
//    * Callback function when the select all checkbox is clicked.
//    *
//    * ```
//    * onSelectAllRows(e) => {}
//    * ```
//    * @param { Object } e: The click event.
//    * @returns { void }
//    */
//   onSelectAllRows: (e: {}) => void,
//   /**
//    * Callback function when a row selected state changes.
//    *
//    * ```
//    * onSelectRow(e, rowProps) => {}
//    * ```
//    * @param { Object } e: The click event.
//    * @param { Object } rowProps: Props of the selected row.
//    * @returns { void }
//    */
//   onSelectRow: (e: {}, rowProps: {}) => void,
//   /** Current page. */
//   page: number,
//   /** Current page size. */
//   pageSize: number,
//   /** An additional row class. */
//   rowClassName: string,
//   /** The component to use for the table rows. */
//   RowComponent: React.Component,
//   /** Array of row data to create rows. */
//   rows: [],
//   /** The row value prop. */
//   rowValueProp: string,
//   /** Array of currently selected rows. */
//   selectedRows: [],
//   /** If rows are selectable. */
//   selectableRows: boolean,
//   /** Current sort column. */
//   orderBy: string,
//   /** Current sort direction. */
//   sorting: string,
//   /** Total number of items in the table. */
//   total: number,
// };

export default class Body extends React.Component {
  static defaultProps = {
    CellComponent: Cell,
    columns: [],
    dockOnBottom: false,
    dockLeftColumn: false,
    HeaderCellComponent: HeaderCell,
    HeaderRowComponent: Row,
    history: routerMiddlewareHistory,
    hoverableRows: true,
    onChangeSort: noop,
    onSelectAllRows: noop,
    onSelectRow: noop,
    RowComponent: Row,
    rows: [],
    rowValueProp: 'id',
    selectedRows: [],
    selectableRows: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      canScrollLeft: false,
      canScrollRight: false,
      canScrollUp: false,
      canScrollDown: false,
    };
  }

  shouldComponentUpdate = (nextProps) => {
    if (didParamsChange(nextProps, this.props)) {
      setTimeout(this.handleScroll, 200);
    }
    return true;
  };

  /**
   * componentDidMount
   * Adds an event listener to the window to adjust overflow scroll shadows if window is resized.
   * @returns { void }
   */
  componentDidMount = () => {
    addEventListener('hashchange', this.handleScroll);
    addEventListener('resize', this.handleScroll);
    addEventListener('resize', this.handleDockBottom);
    addEventListener('scroll', this.handleDockBottom);

    const { history: routeHistory } = this.props;
    this.unlisten = routeHistory.listen(this.updateTableRouteChange);
  };

  /**
   * componentWillUnmount
   * Removes window event listeners.
   * @returns { void }
   */
  componentWillUnmount = () => {
    removeEventListener('hashchange', this.handleScroll);
    removeEventListener('resize', this.handleScroll);
    removeEventListener('resize', this.handleDockBottom);
    removeEventListener('scroll', this.handleDockBottom);
    this.unlisten();
  };

  /**
   * updateTableRouteChange
   * Updates table styles on route change.
   * @returns { void }
   */
  updateTableRouteChange = () => {
    this.resetState();
    this.handleScroll();
  };

  /**
   * resetState
   * Resets the table state.
   * @returns { void }
   */
  resetState = () => {
    this.setState({
      canScrollLeft: false,
      canScrollRight: false,
      canScrollUp: false,
      canScrollDown: false,
    });
  };

  handleRef = (ref) => {
    this.ref = ref;
  };

  handleRefMain = (ref) => {
    this.refMain = ref;
  };

  /**
   * handleScroll
   * Determines if table is scrollable. Updates flags for showing overflow shadows.
   * @returns { void }
   */
  handleScroll = () => {
    if (this.refMain) {
      this.setState({ ...checkIfCanScroll(this.refMain) });
    }
  };

  /**
   * setTableHeight
   * Sets the height of the table body.
   * @param { Number } tableTop: The top position of the table.
   * @param { Number } windowHeight: The height of the window in pixels.
   * @param { Number } tableScrollHeight: The scroll height of the table.
   * @returns { void }
   */
  setTableHeight = (tableTop, windowHeight, tableScrollHeight) => {
    this.refMain.style.height = getTableHeight(tableTop, windowHeight, tableScrollHeight);
  };

  /**
   * handleDockBottom
   * Callback function when docking table to bottom of screen.
   * @returns { void }
   */
  handleDockBottom = () => {
    if (this.ref && this.props.dockOnBottom) {
      const { height } = getElementSizing(document.body);
      const { top } = getElementSizing(this.ref);
      this.setTableHeight(top, height, this.ref.scrollHeight);
    }
  };

  /**
   * Callback function when a row is selected/deselected.
   * @param { Object } e: The click event.
   * @param { Object } rowProps: The props of the row including the updated selected value.
   * @returns { void }
   */
  handleSelectRow = (e, rowProps) => {
    const { onSelectRow, selectedRows, ...props } = this.props;
    const { selected, value } = rowProps;
    if (selected) {
      onSelectRow(e, { ...props, selectedRows: [...selectedRows, value] });
    } else {
      onSelectRow(e, { ...props, selectedRows: selectedRows.filter((val) => val !== value) });
    }
  };

  /**
   * Generates the cell data from formatters defined in the column object.
   * @param { Object } row: The row object data.
   * @param { Object } column: The column definition containing props to format cells.
   * @returns { Object } The cell data.
   */
  generateCellData = (row, column) => {
    const { getTooltip, value, ...cellProps } = column;
    return {
      ...cellProps,
      tooltip: getTooltip ? getTooltip(row) : undefined,
      rowValueKey: value,
      value: row[value],
    };
  };

  generateHeader = () => {
    const {
      bulkActionsContent,
      columns,
      HeaderCellComponent,
      headerCellClassName,
      headerRowClassName,
      HeaderRowComponent,
      onChangeSort,
      onSelectAllRows,
      selectableRows,
      total,
      orderBy,
      selectedRows,
      sorting,
    } = this.props;
    const hasSelectedRows = selectedRows.length > 0;
    return (
      <HeaderRowComponent
        className={`coreTableHeader ${headerRowClassName}`}
        columns={columns}
        role="header"
        key="table-header"
        CellComponent={HeaderCellComponent}
        cellClassName={`coreTableHeaderCell ${headerCellClassName}`}
        cells={this.getColumns()}
        hoverable={false}
        indeterminate={hasSelectedRows && selectedRows.length < total}
        onChangeSort={onChangeSort}
        onSelectRow={onSelectAllRows}
        selected={total > 0 && selectedRows.length === total}
        selectable={selectableRows}
        orderBy={orderBy}
        overlayContent={bulkActionsContent}
        sorting={sorting}
      />
    );
  };

  /**
   * Gets the column array for the table. Inserts docking flag to first column if `dockLeftColumn`.
   * @returns { Array } The table columns.
   */
  getColumns = () => {
    const { columns, dockLeftColumn } = this.props;
    return dockLeftColumn ? [{ ...columns[0], docked: true }, ...columns.slice(1)] : columns;
  };

  /**
   * Generates the table rows.
   * @returns { Array } The table rows.
   */
  generateRows = () => {
    const {
      CellComponent,
      cellClassName,
      columns,
      expandColumn,
      hoverableRows,
      onDoubleClickRow,
      rowClassName,
      RowComponent,
      rows,
      rowValueProp,
      selectedRows,
      selectableRows,
    } = this.props;
    const selectedRowObj = selectedRows.reduce((acc, key) => {
      acc[key] = true;
      return acc;
    }, {});
    return rows.map((row, key) => {
      const cells = this.getColumns().map((column) => this.generateCellData(row, column));
      return (
        <RowComponent
          className={`coreTableRow ${rowClassName}`}
          key={`row-${key}`}
          CellComponent={CellComponent}
          cellClassName={cellClassName}
          cells={cells}
          columns={columns}
          expandColumn={expandColumn}
          hoverable={hoverableRows}
          onDoubleClickRow={onDoubleClickRow}
          rowData={row}
          previousRowData={rows[key - 1]}
          onSelectRow={this.handleSelectRow}
          selected={selectedRowObj[row[rowValueProp]]}
          selectable={selectableRows}
          value={row[rowValueProp]}
        />
      );
    });
  };

  /**
   * getScrollClassNames
   * Gets the classes to toggle the overflow shadows of the table.
   * @returns { Array } List of classes to show the overflow shadows.
   */
  getScrollClassNames = () => {
    const { canScrollLeft, canScrollRight, canScrollUp, canScrollDown } = this.state;
    const { className, dockLeftColumn } = this.props;
    let classes = `${className}`;

    if (dockLeftColumn) {
      classes = `${classes} hasDockingColumn`;
    }
    if (canScrollLeft) {
      classes = `${classes} canScrollLeft`;
    }
    if (canScrollRight) {
      classes = `${classes} canScrollRight`;
    }
    if (canScrollDown) {
      classes = `${classes} canScrollDown`;
    }
    if (canScrollUp) {
      classes = `${classes} canScrollUp`;
    }
    return classes;
  };

  render() {
    const { emptyStateProps, isTableButtonStyle, isToolbarShown, total } = this.props;
    const rows = this.generateRows();
    const showEmptyState = total === 0;

    return (
      <MainCSSContainer
        $isIterableStyle={isTableButtonStyle}
        $isToolbarShown={isToolbarShown}
        className={this.getScrollClassNames()}
        data-role="table"
        key="table"
        data-test="coreTableBody"
      >
        <BodyContainer
          ref={this.handleRefMain}
          onScroll={this.handleScroll}
          data-role="body-wrapper"
        >
          <div className="coreTableBody" ref={this.handleRef} data-role="body" key="table-body">
            {this.generateHeader()}
            {showEmptyState && (
              <>
                <EmptyRowNeededForFirefox />
                <EmptyContainer data-test="emptyState">
                  <span>{emptyStateProps?.text || 'No Results'}</span>
                </EmptyContainer>
              </>
            )}
            {rows}
          </div>
        </BodyContainer>
      </MainCSSContainer>
    );
  }
}
